import { DigitalTwinTranslations, useDigitalTwin } from 'api/digitalTwin/digitalTwin.api'
import { ELPLAN_BID_REFRESH_EVENT_QUERY_KEY } from 'api/elplan/elplan.constants'
import { BidTypeName, ElplanInfoTagForDate } from 'api/elplan/elplan.types'
import { OptJobInputData, useOptJobMutation } from 'api/optJobs/optJobs.api'
import { useUiConfigById } from 'api/uiConfig/uiConfig.api'
import { useHasPermission } from 'helpers/global.helper/global.helper'
import moment from 'moment'
import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import uiConfigStore from 'store/uiConfig/uiConfig'
import LineDivider from 'ui/atoms/Line/LineDivider'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import { OptimizeButton } from 'ui/components/OptimizeFormProvider/components/OptimizeButton/OptimizeButton'
import UiConfig from 'ui/uiConfig/UiConfig'
import UiConfigComponent from 'ui/uiConfig/UiConfigComponent'
import { translateKeyWithSpecificTranslations } from 'utils/stringUtils'
import { useSnapshot } from 'valtio'

import { FormattedBidWithIdAndTitle } from '../../DayAheadBidManagement/DayAheadBidManagement.helper'
import { DayAheadBidEditor } from '../../shared/DayAheadBidEditor/DayAheadBidEditor'
import { ElectricityPriceScenario } from '../ElectricityPriceScenarioModal/types'
import styles from './ElectricityPriceScenarioVisualization.module.less'

const ELECTRICITY_PRICE_SCENARIO_JOB_TYPE = 'electricity_price_scenario'

type ElectricityPriceScenarioVisualizationProps = {
  scenario: ElectricityPriceScenario
  datasetStartTime: ISODateTime
  datasetEndTime: ISODateTime
  translations?: DigitalTwinTranslations | undefined
  disabledBidButtons?: boolean
  tagsForDate?: ElplanInfoTagForDate[]
  bidInputCurrencies?: { block: string; profile: string }
  requireTagForBlockBids?: boolean
  setTypeOfBidToCopy: (bidType: BidTypeName) => void
  setCurrentBlockBid: (bid: FormattedBidWithIdAndTitle) => void
  setCurrentProfileBid: (bid: FormattedBidWithIdAndTitle) => void
}

export default function ElectricityPriceScenarioVisualization({
  scenario,
  datasetStartTime,
  datasetEndTime,
  translations,
  disabledBidButtons,
  tagsForDate,
  bidInputCurrencies,
  requireTagForBlockBids,
  setTypeOfBidToCopy,
  setCurrentBlockBid,
  setCurrentProfileBid,
}: ElectricityPriceScenarioVisualizationProps): ReactElement {
  const {
    hide_optimize_button: hideOptimizeButton,
    optjob_subtypes: scenarioSubtypes,
    scenario_name: scenarioName,
    ui_config_id: uiConfigId,
    uiconfig_override_alias: uiConfigOverrideAlias,
  } = scenario
  const { systemId, activeSystem } = useAuth()
  useUiConfigById(uiConfigId) // Keep this hook call to ensure the UI config data is loaded into the store for downstream usage.
  const { idToUiConfig } = useSnapshot(uiConfigStore)
  const { mutate: optJobCreate, status: jobCreationStatus } = useOptJobMutation()
  const { t } = useTranslation()
  const primaryDigitalTwin = activeSystem?.primary_digital_twin
  const { data: digitalTwin } = useDigitalTwin(primaryDigitalTwin?.uid)

  const hasEditElplanAccess = useHasPermission('change_elplan')
  const [optimizeButtonDisabled, setOptimizeButtonDisabled] = useState(false)
  const optimizeButtonTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    return () => {
      if (optimizeButtonTimeoutRef.current) {
        clearTimeout(optimizeButtonTimeoutRef.current)
      }
    }
  }, [])

  const onOptimize = useCallback(() => {
    setOptimizeButtonDisabled(true)
    const inputData: OptJobInputData = {
      opt_start_time: moment().startOf('hour').toISOString(),
      opt_end_time: undefined,
    }

    if (scenarioSubtypes !== undefined && scenarioSubtypes !== null) {
      inputData.include_subtypes = scenarioSubtypes
    }

    optJobCreate({
      opt_job_type: ELECTRICITY_PRICE_SCENARIO_JOB_TYPE,
      input_data: inputData,
    })

    // Clear any existing timeout
    if (optimizeButtonTimeoutRef.current) {
      clearTimeout(optimizeButtonTimeoutRef.current)
    }

    // Re-enable the button after 3500ms to prevent multiple rapid clicks
    // which can lead to multiple job creation attempts (but will fail from backend).
    optimizeButtonTimeoutRef.current = setTimeout(() => {
      setOptimizeButtonDisabled(false)
      optimizeButtonTimeoutRef.current = null
    }, 3500)
  }, [optJobCreate, scenarioSubtypes])

  const translatedScenarioName = translateKeyWithSpecificTranslations(scenarioName, digitalTwin?.translations)

  const optimizeButtonLabel = useMemo(() => {
    if (scenarioSubtypes === undefined || scenarioSubtypes === null) {
      return t('Optimize all scenarios')
    }

    return `${t('Optimize')} ${translatedScenarioName}`
  }, [scenarioSubtypes, t, translatedScenarioName])

  const translatedCardTitle = useMemo(() => {
    return translateKeyWithSpecificTranslations(uiConfigOverrideAlias.scenario_card_title as string, translations)
  }, [uiConfigOverrideAlias.scenario_card_title, translations])

  return (
    <>
      {!hideOptimizeButton && (
        <div className={styles.ElectricityPriceScenarioVisualization_OptimizaButtonContainer}>
          <OptimizeButton
            disabled={!hasEditElplanAccess || optimizeButtonDisabled}
            type={ELECTRICITY_PRICE_SCENARIO_JOB_TYPE}
            optimizeLabel={!hasEditElplanAccess ? t('You do not have permission to optimize') : optimizeButtonLabel}
            onClick={onOptimize}
            isValid={true}
            subtypes={scenarioSubtypes ?? []}
            creatingOptJob={jobCreationStatus === 'pending'}
          />
        </div>
      )}

      {uiConfigId === null ? (
        <UiConfig
          type="day_ahead_bid_management"
          datasetStartTime={datasetStartTime}
          datasetEndTime={datasetEndTime}
          datasetRefreshToken={ELPLAN_BID_REFRESH_EVENT_QUERY_KEY}
        />
      ) : (
        <UiConfigComponent
          systemId={systemId}
          id={uiConfigId}
          uid={idToUiConfig[uiConfigId]?.uid}
          datasetStartTime={datasetStartTime}
          datasetEndTime={datasetEndTime}
          overrideAlias={{ ...uiConfigOverrideAlias, scenario_card_title: translatedCardTitle }}
          datasetRefreshToken={ELPLAN_BID_REFRESH_EVENT_QUERY_KEY}
        />
      )}

      <LineDivider />

      <DayAheadBidEditor
        filteredBidTypeTypes={scenario.bid_types}
        datasetStartTime={datasetStartTime}
        datasetEndTime={datasetEndTime}
        tagsForDate={tagsForDate}
        bidInputCurrencies={bidInputCurrencies}
        requireTagForBlockBids={requireTagForBlockBids}
        setCurrentBlockBid={setCurrentBlockBid}
        setCurrentProfileBid={setCurrentProfileBid}
        setTypeOfBidToCopy={setTypeOfBidToCopy}
        disabledBidButtons={disabledBidButtons}
        scenarioId={scenario.id}
        showAllScenarioBids={scenario.all_bids}
      />
    </>
  )
}
